import {
  HttpInterceptor,
  HttpErrorResponse,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../environments/environment';
import { SharedService } from '@services/shared.service';
import { SecureStorageService } from '@services/secure-storage.service';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LUQTA_WEBHOOK_URL, MS_TOKEN_KEY } from './micro-api.service';
import { REMOVED_BACKEND_PREFIX } from './public-api-constants';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  authReq: any;
  sharedService = inject(SharedService);
  secureStorage = inject(SecureStorageService);

  public static isSessionExpired = false;

  public static resetSessionExpired() {
    AuthInterceptor.isSessionExpired = false;
  }

  constructor(private router: Router) {}

  private handleAuthError(err: HttpErrorResponse): Observable<any> {
    const errorPayload = err?.error?.payload;
    const errorCode = err?.error?.code;
    const errorMessage = err?.error?.message?.toLowerCase() || '';

    // -------------------------
    // IMPORTANT: Check if this is a public auth endpoint (login, signup, reset password, etc.)
    // Errors on these endpoints (e.g. wrong password on login) should be handled by the component,
    // NOT treated as session expirations.
    // -------------------------
    const url = (err.url || '').toLowerCase();
    const isPublicAuthRequest =
      url.includes('/auth/login') ||
      url.includes('/login') ||
      url.includes('/signup') ||
      url.includes('/register') ||
      url.includes('/reset-password') ||
      url.includes('/reset') ||
      url.includes('/forgot') ||
      url.includes('/verify-otp') ||
      url.includes('/send-otp') ||
      url.includes('/google') ||
      url.includes('/facebook');

    if (isPublicAuthRequest) {
      this.sharedService.hideLoader();
      return throwError(() => err);
    }

    // -------------------------
    // A failure on any host other than the gateway must not destroy the gateway session
    // -------------------------
    // Only the gateway issues and checks this session; a 401 from another service (an AI or
    // third-party endpoint) says nothing about it. The error still propagates to the caller;
    // it just never logs anyone out.
    const microSession = !!localStorage.getItem('msToken');
    const failedElsewhere = !(err.url || '').startsWith(environment.microUrl);
    if (microSession && failedElsewhere) {
      this.sharedService.hideLoader();
      return throwError(() => err);
    }

    // -------------------------
    // Check if user had an active session & current route
    // -------------------------
    const currentUrl = (this.router.url || '').toLowerCase();
    const isAlreadyOnAuthPage =
      currentUrl.includes('/auth/login') ||
      currentUrl.includes('/auth/signup') ||
      currentUrl.includes('/auth/reset');

    const hadToken =
      !!this.secureStorage.getItem('jwtToken') ||
      !!this.secureStorage.getItem('authToken') ||
      !!localStorage.getItem(MS_TOKEN_KEY);

    // -------------------------
    // Handle 401 / 403 / Token Expired
    // -------------------------
    const isRevoked = err?.error?.code === 'TOKEN_REVOKED';
    const isTokenError = (
      err?.error?.reason === 'INVALID_TOKEN' ||
      isRevoked ||
      errorMessage.includes('token expired') ||
      errorMessage.includes('expired token') ||
      errorMessage.includes('invalid token') ||
      errorMessage.includes('token is expired') ||
      errorMessage.includes('jwt expired') ||
      errorMessage.includes('unauthorized access') ||
      errorMessage.includes('different datasource') ||
      errorMessage.includes('datasource')
    );

    if (
      (err.status === 401 || errorCode === 'ERROR_CODE_UNAUTHORIZED') &&
      (isTokenError || errorMessage.includes('datasource'))
    ) {
      if (!AuthInterceptor.isSessionExpired && hadToken && !isAlreadyOnAuthPage) {
        AuthInterceptor.isSessionExpired = true;

        this.clearStorage();
        this.sharedService.hideLoader();
        this.sharedService.closeAllModals();

        const message = isRevoked
          ? 'This session has ended. Please login again.'
          : errorMessage.includes('datasource')
            ? 'Invalid authentication token. Please login again.'
            : 'Your session has expired. Please login again.';

        this.sharedService.createNotification('error', message);
        this.router.navigate(['/auth/login']);
      } else {
        this.clearStorage();
        this.sharedService.hideLoader();
      }

      return EMPTY;
    }

    // -------------------------
    // Handle 403 separately (Access Forbidden)
    // -------------------------
    if (err.status === 403) {
      if (errorCode === 'ERROR_CODE_ACCESS_DENIED') {
        this.handleAccessDenied(errorPayload);
        return throwError(() => err);
      }

      if (isTokenError) {
        if (!AuthInterceptor.isSessionExpired && hadToken && !isAlreadyOnAuthPage) {
          AuthInterceptor.isSessionExpired = true;
          this.clearStorage();
          this.sharedService.hideLoader();
          this.sharedService.closeAllModals();

          this.sharedService.createNotification(
            'error',
            'Your session has expired. Please login again.'
          );
          this.router.navigate(['/auth/login']);
        } else {
          this.clearStorage();
          this.sharedService.hideLoader();
        }

        return EMPTY;
      }
    }

    // -------------------------
    // Onboarding-related redirects
    // -------------------------
    if (errorCode === 'ERROR_CODE_ACCESS_DENIED') {
      this.handleAccessDenied(errorPayload);
      return throwError(() => err);
    }

    // -------------------------
    // Handle Fatal Error
    // -------------------------
    if (errorCode === 'ERROR_FATAL') {
      this.sharedService.hideLoader();
      return throwError(() => err);
    }

    // -------------------------
    // Handle generic 401 errors
    // -------------------------
    if (err.status === 401) {
      if (!AuthInterceptor.isSessionExpired && hadToken && !isAlreadyOnAuthPage) {
        AuthInterceptor.isSessionExpired = true;
        this.clearStorage();
        this.sharedService.hideLoader();
        this.sharedService.closeAllModals();

        this.sharedService.createNotification(
          'error',
          err?.error?.message || 'Authentication failed. Please login again.'
        );
        this.router.navigate(['/auth/login']);
      } else {
        this.clearStorage();
        this.sharedService.hideLoader();
      }

      return EMPTY;
    }

    // -------------------------
    // Other errors - Let the component handle them
    // -------------------------
    this.sharedService.hideLoader();
    return throwError(() => err);
  }

  private handleAccessDenied(errorPayload: any): void {
    this.sharedService.hideLoader();

    if (errorPayload) {
      if (
        errorPayload.company_status === "user don't have a registered company"
      ) {
        this.sharedService.createNotification(
          'info',
          'Please complete your company setup'
        );
        setTimeout(() => this.router.navigate(['/auth/setup-company']), 2000);
      } else if (
        errorPayload.subscription_status === 'user in not subscribed yet'
      ) {
        this.sharedService.createNotification(
          'info',
          'Please subscribe to a plan'
        );
        setTimeout(() => this.router.navigate(['/auth/choose-plan']), 2000);
      } else if (
        errorPayload.verification_status ===
        'user need to verify there account using top'
      ) {
        this.sharedService.createNotification(
          'info',
          'Please verify your account using OTP'
        );
        setTimeout(() => this.router.navigate(['/auth/verify-otp']), 2000);
      } else {
        this.sharedService.createNotification(
          'error',
          'Access denied. Please contact support.'
        );
        setTimeout(() => this.router.navigate(['/auth/login']), 2000);
      }
    }
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip interceptor for AI API calls (they have their own authentication)
    if (req.url.includes('ai.luqta.io')) {
      return next.handle(req).pipe(
        catchError((x) => throwError(() => x)) // Don't trigger session expiry for AI API errors
      );
    }

    // Skip interceptor for ChatBolt API calls (uses x-api-key auth)
    if (req.url.includes('/api/v1/api-builder/')) {
      return next.handle(req).pipe(
        catchError((x) => throwError(() => x))
      );
    }
    if (req.url.includes('/api/v1/ai-endpoints/')) {
      return next.handle(req).pipe(
        catchError((x) => throwError(() => x))
      );
    }
    // The platform webhook carries a developer API key, not the session (luqta-webhook tester).
    if (req.url.startsWith(LUQTA_WEBHOOK_URL)) {
      return next.handle(req);
    }

    // Xano is removed from the dashboard. Should anything still build a request to it (an old
    // PublicApi URL or a hard-coded Xano host), it is refused here and never reaches the network.
    // The one exception is the WhatsApp Campaigns list, which has no gateway action yet.
    const allowedXano = req.url.startsWith(environment.whatsappCampaignsUrl);
    if (req.url.startsWith(REMOVED_BACKEND_PREFIX) || (/xano\.io/i.test(req.url) && !allowedXano)) {
      console.error('[AuthInterceptor] Blocked a request to the removed Xano backend:', req.url);
      return throwError(() => new HttpErrorResponse({
        error: { message: 'This feature is not available yet.', code: 'NOT_ON_GATEWAY' },
        status: 501,
        statusText: 'Not Implemented',
        url: req.url,
      }));
    }

    const token = this.secureStorage.getItem('jwtToken')!;
    this.authReq = req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });

    return next.handle(this.authReq).pipe(
      map((event) => {
        // The microservice answers a dead token with HTTP 201 and
        // { isSuccess: false, reason: 'INVALID_TOKEN' }, so it never reached the 401 handling
        // below: the user stayed "logged in" on empty pages, and saving failed with misleading
        // errors such as "Company data not found". Re-raise it as the 401 it really is.
        if (event instanceof HttpResponse && this.isMicroTokenFailure(event)) {
          throw new HttpErrorResponse({
            error: event.body,
            headers: event.headers,
            status: 401,
            statusText: 'Unauthorized',
            url: event.url ?? undefined,
          });
        }
        return event;
      }),
      catchError((x) => this.handleAuthError(x))
    );
  }

  private isMicroTokenFailure(res: HttpResponse<any>): boolean {
    const body = res.body;
    if (body?.isSuccess !== false || !(res.url || '').startsWith(environment.microUrl)) {
      return false;
    }
    const message = String(body?.message || '').toLowerCase();
    return (
      body?.reason === 'INVALID_TOKEN' ||
      // A revoked session (logout elsewhere, password reset) is just as dead as an expired one.
      body?.code === 'TOKEN_REVOKED' ||
      message.includes('expired token') ||
      message.includes('invalid token') ||
      message.includes('token expired')
    );
  }

  clearStorage() {
    this.secureStorage.clearProtected();
    localStorage.removeItem(MS_TOKEN_KEY);
    sessionStorage.clear();
    // Remove orphaned UUID→contestId entries from localStorage
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    Object.keys(localStorage).forEach(key => {
      if (uuidPattern.test(key)) localStorage.removeItem(key);
    });
  }
}
